// src/pages/RoutesIndex.jsx
import { Link as RouterLink } from "react-router-dom";
import {
    Container,
    Typography,
    Box,
    Paper,
    List,
    ListItem,
    ListItemText,
    Link as MuiLink,
    Chip,
} from "@mui/material";

// 📍 Routes statiques (pas de :param) — cliquables, lien direct.
// path: chemin réel ; label: nom affiché ; roles: rôles autorisés (null = public).
const STATIC_SECTIONS = [
    {
        title: "Pages publiques",
        routes: [
            { path: "/", label: "Accueil", roles: null },
            { path: "/calendrier", label: "Calendrier des évènements", roles: null },
            { path: "/prestations", label: "Prestations", roles: null },
            { path: "/jobs", label: "Jobs", roles: null },
            { path: "/a-propos", label: "À propos", roles: null },
            { path: "/mentions-legales", label: "Mentions légales", roles: null },
            { path: "/login", label: "Connexion", roles: null },
        ],
    },
    {
        title: "Chronométreur / Manuel",
        routes: [
            { path: "/protocole", label: "Protocole", roles: ["chronometreur", "manuel"] },
            { path: "/simulator", label: "Simulateur 4 canaux", roles: ["chronometreur", "manuel"] },
        ],
    },
    {
        title: "Chronométreur",
        routes: [
            { path: "/timer", label: "Connexion chrono", roles: ["chronometreur"] },
            { path: "/timeserver", label: "Serveur de temps", roles: ["chronometreur"] },
            { path: "/live-overview", label: "Vue d'ensemble live", roles: ["chronometreur"] },
            { path: "/documents", label: "Documents (listes de départ, résultats)", roles: ["chronometreur"] },
            { path: "/calendrier-admin", label: "Gestion du calendrier", roles: ["chronometreur"] },
            { path: "/timingsoftware", label: "Timing Software — hub", roles: ["chronometreur"] },
            { path: "/parallel", label: "Courses parallèles — hub", roles: ["chronometreur", "colour", "paralleltimer"] },
            { path: "/parallel-archives", label: "Courses parallèles — archives", roles: ["chronometreur"] },
            { path: "/parallel-config", label: "Courses parallèles — configuration", roles: ["chronometreur"] },
            { path: "/routes", label: "Cette page (liste des routes)", roles: ["chronometreur"] },
        ],
    },
    {
        title: "Overlays stream (OBS)",
        routes: [
            { path: "/stream/startlist", label: "Overlay — liste de départ", roles: null },
            { path: "/stream/duel", label: "Overlay — duel", roles: null },
            { path: "/stream/ranking", label: "Overlay — classement", roles: null },
        ],
    },
];

// 📍 Routes avec paramètre dynamique — pas de lien direct possible sans une
// vraie valeur (codex, catégorie, clé d'évènement...), juste listées pour
// référence, groupées par thème.
const DYNAMIC_SECTIONS = [
    {
        title: "Live / évènement (:codex)",
        routes: [
            { path: "/live/:codex", label: "Live timing (public)" },
            { path: "/live/:codex/startlist", label: "Liste de départ (public)" },
            { path: "/live/:codex/speaker", label: "Vue speaker (public, code d'accès)" },
            { path: "/live/:codex/config", label: "Configuration de l'évènement", roles: ["chronometreur"] },
        ],
    },
    {
        title: "Courses parallèles (:category)",
        routes: [
            { path: "/parallel-live/:category", label: "Vue live (public)" },
            { path: "/parallel-live/:category/speaker", label: "Vue speaker (public)" },
            { path: "/bibs/:category", label: "Dossards", roles: ["colour", "chronometreur"] },
            { path: "/brackets/:category", label: "Tableau (brackets)", roles: ["paralleltimer", "chronometreur"] },
            { path: "/athletes-import/:category", label: "Import athlètes", roles: ["chronometreur"] },
            { path: "/qualifications-import/:category", label: "Import qualifications", roles: ["chronometreur"] },
            { path: "/parallel-config/:category", label: "Configuration", roles: ["chronometreur"] },
            { path: "/parallel-fis/:category", label: "Export FIS", roles: ["chronometreur"] },
        ],
    },
    {
        title: "Timing Software (:eventKey)",
        routes: [
            { path: "/timingsoftware/athletes-import/:eventKey", label: "Import athlètes", roles: ["chronometreur"] },
            { path: "/timingsoftware/startlist/:eventKey", label: "Liste de départ", roles: ["chronometreur"] },
            { path: "/timingsoftware/athletes/:eventKey", label: "Liste des athlètes", roles: ["chronometreur"] },
            { path: "/timingsoftware/chrono/:eventKey", label: "Chronométrage", roles: ["chronometreur"] },
            { path: "/timingsoftware/swissski/:eventKey", label: "Export Swiss-Ski", roles: ["chronometreur"] },
            { path: "/timingsoftware/fis/:eventKey", label: "Panel FIS", roles: ["chronometreur"] },
            { path: "/timingsoftware/resultats-telemark/:eventKey", label: "Résultats télémark", roles: ["scribe", "chronometreur"] },
        ],
    },
    {
        title: "Saisie pénalités télémark (:eventKey)",
        routes: [
            { path: "/saisiepenalite/:eventKey", label: "Hub de saisie", roles: ["scribe", "chronometreur"] },
            { path: "/saisiepenalite/:eventKey/overview", label: "Vue d'ensemble — contrôle et correction", roles: ["scribe", "chronometreur"] },
            { path: "/saisiepenalite/:eventKey/solo", label: "Saisie solo", roles: ["scribe", "chronometreur"] },
            { path: "/saisiepenalite/:eventKey/:category/:parity/:slot", label: "Saisie d'un duel", roles: ["scribe", "chronometreur"] },
        ],
    },
];

function RoleChips({ roles }) {
    if (!roles) return <Chip label="Public" size="small" variant="outlined" />;
    return (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {roles.map((r) => (
                <Chip key={r} label={r} size="small" />
            ))}
        </Box>
    );
}

export default function RoutesIndex() {
    return (
        <Container maxWidth="md" sx={{ mt: 6, mb: 6 }}>
            <Typography variant="h4" sx={{ mb: 1 }}>
                Toutes les routes
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4 }}>
                Index de navigation — pratique pour retrouver une page sans se souvenir de l'URL exacte.
            </Typography>

            {STATIC_SECTIONS.map((section) => (
                <Paper key={section.title} sx={{ mb: 3, p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {section.title}
                    </Typography>
                    <List dense>
                        {section.routes.map((r) => (
                            <ListItem
                                key={r.path}
                                secondaryAction={<RoleChips roles={r.roles} />}
                                sx={{ borderBottom: "1px solid #f0f0f0" }}
                            >
                                <ListItemText
                                    primary={
                                        <MuiLink component={RouterLink} to={r.path}>
                                            {r.label}
                                        </MuiLink>
                                    }
                                    secondary={r.path}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            ))}

            <Typography variant="h5" sx={{ mt: 5, mb: 1 }}>
                Routes avec paramètre
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
                Nécessitent une valeur réelle (codex, catégorie, clé d'évènement...) — pas de lien direct possible ici.
            </Typography>

            {DYNAMIC_SECTIONS.map((section) => (
                <Paper key={section.title} sx={{ mb: 3, p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {section.title}
                    </Typography>
                    <List dense>
                        {section.routes.map((r) => (
                            <ListItem
                                key={r.path}
                                secondaryAction={<RoleChips roles={r.roles} />}
                                sx={{ borderBottom: "1px solid #f0f0f0" }}
                            >
                                <ListItemText primary={r.label} secondary={r.path} />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            ))}
        </Container>
    );
}